// LAYOUT-SPEC §0 — one deliverable across periods (wireframe 4)
//
//   ONE QUESTION  What does the contract say about this deliverable, who owes
//                 it in each window, and how is each date doing?
//   PRIMARY OBJECT  One duty (Archetype B + C: the dates table opens the date
//                 drawer).
//   PRIMARY VERB    Edit rule (the fix for "needs a start date"); + Add a date.
//   WORST-CASE VOLUME  Monthly across five periods and a few teams: ~120 dates.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraBadge, SpecraBreadcrumbs, SpecraButton, SpecraCheckbox, SpecraDateField, SpecraDetailPage, SpecraEmptyState, SpecraEyebrow, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraKeyValueRow, SpecraLink, SpecraPageHeader, SpecraSearchField, SpecraSectionHeader, SpecraSelect, SpecraText, specraFocusRing } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageBody } from '../../../../components/layout';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { ErrorState } from '../../_shared/components/PageStates';
import { GlossaryText, useGlossaryCasing } from '../../Glossary/GlossaryText';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useCmAccess } from '../access';
import { AwaitingApprovalBanner } from '../components/AwaitingApproval';
import { CmDrawer } from '../components/CmDrawer';
import { useColumnView } from '../components/ColumnMenu';
import { CommentThread } from '../components/CommentThread';
import { CmLatestSubmission, CmOccurrenceStatus, CmSourceButton, CmTable, DutyClinLabel, DutySourceChips, occurrenceClinDisplay } from '../components/common';
import { DeliverableFilters } from '../components/DeliverableFilters';
import { LogEventDrawer } from '../components/LogEventDrawer';
import { AroSuggestion, RuleEditorDrawer } from '../components/RuleEditor';
import { WaiveDatesDrawer } from '../components/WaiveDatesDrawer';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, useAddOccurrence, useAwaitingApproval, useDeleteDuty, useDuty, useOccurrences, useWorkProducts } from '../hooks';
import { dutyClinDisplay } from '../lib/dutyClin';
import { cmDate, cmSpan } from '../lib/format';
import { describeRule, dutyTaskNumber, eventLabel, eventLabelIn, eventTexts, groupRules, mintReceipt } from '../lib/rule';
import { CONDITION_NOT_SETTLED, conditionHeldStatus, isOccurrenceState, STATE_SHORT_LABEL, tierLabel } from '../lib/status';
import { filterDateRows, normalizeTeamId, resolveOccurrencePeriodId, soleTeam } from '../lib/tableControls';
import { useCmLookups, useConditionHolds } from '../lookups';
import { DutyFormDrawer } from './DeliverablesPage';
const FIX_LABEL = {
    UpdateScheduleRule: 'Edit the rule',
    CreateScheduleRule: 'Add a rule',
    LogContractEvent: 'Log the event date',
    UpdateDeliverableSettings: 'Open program settings',
    SetDutyAssignment: 'Open the team',
    RuleSowGap: 'Open the team',
    UpdateDuty: 'Edit the deliverable'
};
// Late, Upcoming and Approved: the three counts a team line shows. Waived and
// not_owed dates owe nothing, so they are counted in none.
const LATE_STATES = new Set([
    'late',
    'rejected'
]);
const UPCOMING_STATES = new Set([
    'due_soon',
    'upcoming',
    'waiting_on_event',
    'scheduled',
    'held',
    'awaiting_review',
    'due_unknown'
]);
export function stateCounts(occs) {
    const count = (states)=>occs.filter((o)=>states.has(o.status?.state ?? '')).length;
    return {
        late: count(LATE_STATES),
        upcoming: count(UPCOMING_STATES),
        approved: count(new Set([
            'approved'
        ]))
    };
}
// checkRowId keys a check row. The message is part of it: a duty the award
// schedules in several periods the contract lacks carries one
// period_not_stated check per period, alike in every other field (SAD-775).
export function checkRowId(c) {
    return `${c.code}-${c.ruleId}-${c.workstreamId}-${c.periodOfPerformanceId}-${c.exerciseId}-${c.fromOn}-${c.message}`;
}
// A check's fix target: the rule or team its fix opens. Checks that differ
// only in what they affect (one per exercise, say) share a target and a row;
// checks whose fixes open different rules or teams never do.
function fixTarget(c) {
    switch(c.fixVerb){
        case 'UpdateScheduleRule':
        case 'CreateScheduleRule':
            return c.ruleId;
        case 'SetDutyAssignment':
        case 'RuleSowGap':
            return c.workstreamId;
        default:
            return '';
    }
}
/** groupDutyChecks folds identical checks (same message and fix) into one row. */ export function groupDutyChecks(checks) {
    const groups = new Map();
    for (const c of checks){
        const message = c.message || c.code.replace(/_/g, ' ');
        const key = `${c.code}|${c.fixVerb}|${fixTarget(c)}|${message}`;
        const g = groups.get(key) ?? {
            key,
            message,
            checks: []
        };
        g.checks.push(c);
        groups.set(key, g);
    }
    return [
        ...groups.values()
    ];
}
/** checkAffects names what one check of a group is about. */ export function checkAffects(c, lk) {
    const ex = lk.exerciseById.get(c.exerciseId);
    const ot = ex ? lk.optionalTaskById.get(ex.optionalTaskId) : undefined;
    const pop = lk.periodById.get(c.periodOfPerformanceId);
    const where = ex ? `${ot?.short ? `${ot.short} ` : ''}exercise ${ex.clinNumber} (${cmSpan(ex.start, ex.end)})` : pop ? `${pop.name} (${cmSpan(pop.start, pop.end)})` : c.fromOn ? cmSpan(c.fromOn, c.toOn) : '';
    const team = c.workstreamId ? lk.teamName(c.workstreamId) : '';
    return [
        team,
        where
    ].filter(Boolean).join(' · ') || 'The whole deliverable';
}
export function windowGroups(occs, lk) {
    const groups = new Map();
    for (const o of occs){
        const ex = lk.exerciseById.get(o.exerciseId);
        const pop = lk.periodById.get(o.periodOfPerformanceId);
        // No exercise and no period: a rule-generated date is owed contract-wide;
        // one with no rule was added by hand and has no window to sit in.
        if (!ex && !pop && !o.ruleId) continue;
        const whole = !ex && !pop;
        const key = whole ? 'whole' : ex ? `ex:${ex.start}:${ex.end}` : `pop:${pop?.id}`;
        const label = whole ? 'Whole contract' : ex ? `Window ${cmSpan(ex.start, ex.end)}` : `${pop?.name} · ${cmSpan(pop?.start, pop?.end)}`;
        const g = groups.get(key) ?? {
            label,
            start: whole ? '' : ex?.start ?? pop?.start ?? '9',
            byTeam: new Map()
        };
        const teamKey = `${o.workstreamId}|${o.exerciseId}`;
        const arr = g.byTeam.get(teamKey) ?? [];
        arr.push(o);
        g.byTeam.set(teamKey, arr);
        groups.set(key, g);
    }
    return [
        ...groups.entries()
    ].sort((a, b)=>a[1].start.localeCompare(b[1].start)).map(([key, g])=>({
            key,
            label: g.label,
            lines: [
                ...g.byTeam.entries()
            ].map(([k, list])=>{
                const [ws, exId] = k.split('|');
                return {
                    // The grouping's own ids: two lines can read alike (an unloaded
                    // team, an exercise with no CLIN) and still be two lines.
                    key: k,
                    team: lk.teamName(ws),
                    exercise: lk.exerciseById.get(exId)?.clinNumber ?? '',
                    ...stateCounts(list)
                };
            })
        }));
}
/** States a waiver can no longer be proposed in. */ const NOT_WAIVABLE = new Set([
    'waived',
    'not_owed',
    'retired'
]);
/**
 * SAD-861: a date a waiver may still be proposed for: a stored date (never a
 * projected one), not waived or closed, with no proposal awaiting approval.
 */ export function isWaivable(o, openDecision) {
    return !!o.id && !o.event?.projected && !NOT_WAIVABLE.has(o.status?.state ?? '') && !o.waivedOn && !openDecision(o.id);
}
/** SAD-861: each window's dates (a period, or an exercise), for a whole-period waiver. */ export function waiveGroups(occs, lk) {
    const groups = new Map();
    for (const o of occs){
        const ex = lk.exerciseById.get(o.exerciseId);
        const pop = lk.periodById.get(o.periodOfPerformanceId);
        if (!ex && !pop) continue;
        const key = ex ? `ex:${ex.start}:${ex.end}` : `pop:${pop?.id}`;
        const label = ex ? `Every open date ${cmSpan(ex.start, ex.end)}` : `Every open date in ${pop?.name}`;
        const g = groups.get(key) ?? {
            key,
            label,
            ids: [],
            start: ex?.start ?? pop?.start ?? '9'
        };
        g.ids.push(o.id);
        groups.set(key, g);
    }
    return [
        ...groups.values()
    ].sort((a, b)=>a.start.localeCompare(b.start)).map(({ key, label, ids })=>({
            key,
            label,
            ids
        }));
}
export function DutyPage() {
    const { dutyId = '' } = useParams();
    const { programId } = useProgramContext();
    // SAD-859: a deliverable promoted from a logged work product keeps it as
    // its history (the work product's promoted_to_duty_id, read back).
    const promotedFrom = useWorkProducts(programId, {
        promotedToDutyId: dutyId
    }, {
        enabled: !!dutyId
    }).data?.[0];
    const access = useCmAccess(programId);
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const clinsQ = useContractCLINs(programId);
    const clins = clinsQ.data?.clins ?? [];
    const casing = useGlossaryCasing();
    const duty = useDuty(programId, dutyId);
    const dates = useOccurrences(programId, {
        dutyId,
        window: OccurrenceWindow.ALL,
        includeProjected: true
    });
    const [editRule, setEditRule] = useState(null);
    const [editDuty, setEditDuty] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const navigate = useNavigate();
    const deleteDuty = useDeleteDuty(programId, {
        success: 'Deliverable deleted',
        failure: 'Could not delete'
    });
    const [addDate, setAddDate] = useState(false);
    const [logKind, setLogKind] = useState(null);
    // SAD-963: the team the event is logged for: the check's own team, or the
    // one team all this milestone's dates belong to.
    const [logTeam, setLogTeam] = useState('');
    const openLog = (kind, team)=>{
        setLogTeam(team);
        setLogKind(kind);
    };
    const [receipt, setReceipt] = useState(null);
    const [dateQuery, setDateQuery] = useState('');
    const [periodFilter, setPeriodFilter] = useState('');
    // SAD-861: dates picked for a waiver, and the waiver drawer.
    const [selected, setSelected] = useState(new Set());
    const [waiving, setWaiving] = useState(false);
    const { byOccurrence } = useAwaitingApproval(programId);
    const [checksOpen, setChecksOpen] = useState(false);
    const checksId = useId();
    const [expandedChecks, setExpandedChecks] = useState(new Set());
    const d = duty.data?.duty;
    const rules = duty.data?.rules ?? [];
    const ruleGroups = groupRules(rules, lk.rulePlace);
    const task = dutyTaskNumber(rules, lk.rulePlace);
    const holdOf = useConditionHolds(programId, (duty.data?.checks ?? []).some((c)=>c.code === CONDITION_NOT_SETTLED));
    const checks = duty.data?.checks ?? [];
    const assignments = duty.data?.assignments ?? [];
    const occs = useMemo(()=>[
            ...dates.data?.occurrences ?? []
        ].sort((a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9')), [
        dates.data
    ]);
    // SAD-967: an on_request rule (the award's or a SOW's) is due after the
    // government asks; the request is logged for the team whose rule it is, or
    // the one team all its dates belong to.
    const requestTeams = assignments.filter((a)=>a.teamRules.some((r)=>r.eventKind === 'on_request')).map((a)=>lk.undatedReason(dutyId, a.id)?.workstreamId ?? '').filter((id, i, all)=>id && all.indexOf(id) === i);
    const onRequest = requestTeams.length > 0 || rules.some((r)=>r.eventKind === 'on_request');
    const requestTeam = requestTeams.length === 1 ? requestTeams[0] : soleTeam(occs.map((o)=>o.workstreamId));
    const [requesting, setRequesting] = useState(false);
    const dateControlRows = useMemo(()=>{
        return occs.map((o)=>{
            const teamId = normalizeTeamId(o.workstreamId);
            const periodId = resolveOccurrencePeriodId(o, lk.periods, lk.exercises);
            const clin = occurrenceClinDisplay(d?.dutyKey ?? '', periodId, lk, clins);
            return {
                occ: o,
                dueOn: o.dueOn,
                clinLabel: clin.label,
                teamId,
                teamName: lk.teamName(teamId),
                statusState: o.status?.state,
                periodId,
                title: d?.title,
                pwsRef: d?.pwsRef,
                dutyKey: d?.dutyKey,
                sourceDocumentId: d?.sourceDocumentId
            };
        });
    }, [
        occs,
        lk,
        d,
        clins
    ]);
    const searchedControls = useMemo(()=>filterDateRows(dateControlRows, {
            q: dateQuery,
            period: periodFilter
        }, clins), [
        dateControlRows,
        dateQuery,
        periodFilter,
        clins
    ]);
    const dateSpecs = [
        {
            id: 'due',
            label: 'Due',
            kind: 'date',
            value: (o)=>cmDate(o.dueOn, 'Not stated'),
            sortValue: (o)=>o.dueOn
        },
        {
            id: 'team',
            label: 'Team',
            value: (o)=>lk.teamName(o.workstreamId)
        },
        {
            id: 'clin',
            label: 'CLIN',
            value: (o)=>occurrenceClinDisplay(d?.dutyKey ?? '', o.periodOfPerformanceId, lk, clins).label
        },
        {
            id: 'latest',
            label: 'Latest submission',
            kind: 'date',
            value: (o)=>cmDate(o.latestSubmission?.submittedOn, 'None'),
            sortValue: (o)=>o.latestSubmission?.submittedOn ?? ''
        },
        {
            id: 'status',
            label: 'Status',
            value: (o)=>isOccurrenceState(o.status?.state ?? '') ? STATE_SHORT_LABEL[o.status?.state] : o.status?.state ?? ''
        }
    ];
    const dateView = useColumnView(dateSpecs, occs);
    const searchedDates = searchedControls.map((r)=>r.occ);
    const shown = dateView.apply(searchedDates);
    const waivable = occs.filter((o)=>isWaivable(o, (id)=>byOccurrence.has(id)));
    const waivableIds = new Set(waivable.map((o)=>o.id));
    const shownWaivable = shown.filter((o)=>waivableIds.has(o.id));
    const picked = [
        ...selected
    ].filter((id)=>waivableIds.has(id));
    const toggle = (id, on)=>setSelected((prev)=>{
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });
    const periodOptions = lk.periods.map((p)=>({
            value: p.id,
            label: p.name
        }));
    if (duty.error) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(ErrorState, {
                error: duty.error,
                title: "Could not load this deliverable",
                onRetry: ()=>void duty.refetch()
            })
        });
    }
    const needsStart = checks.filter((c)=>c.code === 'needs_start_date');
    const conditionHeld = checks.some((c)=>c.code === CONDITION_NOT_SETTLED);
    const otherChecks = checks.filter((c)=>c.code !== 'needs_start_date' && c.code !== 'waiting_on_event' && c.code !== CONDITION_NOT_SETTLED);
    const kindOfRule = (ruleId)=>rules.find((r)=>r.id === ruleId)?.eventKind ?? '';
    const eventName = (kind)=>eventLabelIn(kind, lk.duties, lk.rules, casing);
    // SAD-869: an event a milestone dates (the kickoff meeting) is marked held.
    const isMilestone = (kind)=>!!kind && lk.duties.some((x)=>x.eventKind === kind);
    const logLabel = (kind)=>isMilestone(kind) ? `Mark ${eventName(kind)} held` : `Log ${eventName(kind)}`;
    const checkGroups = groupDutyChecks(otherChecks);
    const fix = (c)=>{
        switch(c.fixVerb){
            case 'UpdateScheduleRule':
            case 'CreateScheduleRule':
                {
                    const r = rules.find((x)=>x.id === c.ruleId);
                    setEditRule(r ?? 'new');
                    return;
                }
            case 'UpdateDuty':
                setEditDuty(true);
                return;
            default:
        }
    };
    const fixLink = (c)=>{
        if (c.fixVerb === 'UpdateDeliverableSettings') return `/delivery/programs/${programId}/settings`;
        if ((c.fixVerb === 'SetDutyAssignment' || c.fixVerb === 'RuleSowGap') && c.workstreamId) return `/delivery/programs/${programId}/workstreams/${c.workstreamId}`;
        return null;
    };
    const allShownPicked = shownWaivable.length > 0 && shownWaivable.every((o)=>selected.has(o.id));
    const selectColumn = {
        id: 'select',
        header: /*#__PURE__*/ _jsx(SpecraCheckbox, {
            "aria-label": "Pick every date a waiver can be proposed for",
            checked: allShownPicked ? true : picked.length > 0 ? 'indeterminate' : false,
            disabled: shownWaivable.length === 0,
            onCheckedChange: (v)=>// Only the dates shown change: a pick hidden by a filter is kept.
                setSelected((prev)=>{
                    const next = new Set(prev);
                    for (const o of shownWaivable){
                        if (v === true) next.add(o.id);
                        else next.delete(o.id);
                    }
                    return next;
                }),
            "data-testid": Testids.cmDuty.dateSelectAll
        }),
        cell: (o)=>waivableIds.has(o.id) ? /*#__PURE__*/ _jsx(SpecraCheckbox, {
                "aria-label": `Pick ${cmDate(o.dueOn)}`,
                checked: selected.has(o.id),
                onCheckedChange: (v)=>toggle(o.id, v === true),
                // Picking a date never opens its drawer.
                onClick: (e)=>e.stopPropagation(),
                "data-testid": Testids.cmDuty.dateSelect,
                "data-row-id": o.id
            }) : null
    };
    const columns = [
        ...access.canWrite && waivable.length > 0 ? [
            selectColumn
        ] : [],
        {
            id: 'due',
            header: dateView.header('due'),
            width: 220,
            cell: (o)=>/*#__PURE__*/ _jsxs("span", {
                    className: "tabular-nums",
                    children: [
                        cmDate(o.dueOn, 'Not stated'),
                        o.label ? /*#__PURE__*/ _jsx("span", {
                            className: "ml-2 text-muted-foreground",
                            children: o.label
                        }) : null,
                        o.event?.projected ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "info",
                            className: "ml-2",
                            "data-testid": Testids.cmDuty.projectedBadge,
                            children: "Projected"
                        }) : null
                    ]
                })
        },
        {
            id: 'team',
            header: dateView.header('team'),
            width: 160,
            cell: (o)=>lk.teamName(o.workstreamId)
        },
        {
            id: 'clin',
            header: dateView.header('clin'),
            width: 130,
            cell: (o)=>/*#__PURE__*/ _jsx(DutyClinLabel, {
                    display: occurrenceClinDisplay(d?.dutyKey ?? '', o.periodOfPerformanceId, lk, clins),
                    tone: "primary",
                    testId: Testids.cmDuty.dateClin
                })
        },
        {
            id: 'latest',
            header: dateView.header('latest'),
            headerClassName: 'whitespace-nowrap',
            cell: (o)=>/*#__PURE__*/ _jsx(CmLatestSubmission, {
                    occ: o
                })
        },
        {
            id: 'status',
            header: dateView.header('status'),
            width: 180,
            cell: (o)=>/*#__PURE__*/ _jsx(CmOccurrenceStatus, {
                    programId: programId,
                    occ: o,
                    finalReviewTier: d?.finalReviewTier
                })
        },
        {
            id: 'open',
            header: /*#__PURE__*/ _jsx("span", {
                className: "sr-only",
                children: "Open"
            }),
            width: 80,
            cell: ()=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center text-muted-foreground",
                    children: [
                        "Open",
                        /*#__PURE__*/ _jsx(ChevronRight, {
                            className: "size-4",
                            "aria-hidden": "true"
                        })
                    ]
                })
        }
    ];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraDetailPage, {
                "data-testid": Testids.cmDuty.page,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
                            renderLink: renderRouterLink,
                            items: [
                                {
                                    id: 'deliverables',
                                    label: 'Deliverables',
                                    href: `/delivery/programs/${programId}/deliverables`
                                },
                                {
                                    id: 'duty',
                                    label: d?.title ?? 'Deliverable',
                                    current: true
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraPageHeader, {
                            titleSize: "home",
                            title: d ? /*#__PURE__*/ _jsx(GlossaryText, {
                                text: d.title
                            }) : 'Deliverable',
                            actions: access.canWrite && d ? /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    d.eventKind ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>openLog(d.eventKind, soleTeam(occs.map((o)=>o.workstreamId))),
                                        "data-testid": Testids.cmDuty.logEvent,
                                        children: "Mark held"
                                    }) : null,
                                    onRequest ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setRequesting(true),
                                        "data-testid": Testids.cmDuty.logRequest,
                                        children: "Log government request"
                                    }) : null,
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setEditDuty(true),
                                        children: "Edit deliverable"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "destructive",
                                        onClick: ()=>setConfirmDelete(true),
                                        "data-testid": Testids.cmDuty.delete,
                                        children: "Delete deliverable"
                                    })
                                ]
                            }) : undefined
                        })
                    ]
                }),
                aside: d ? /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.cmDuty.header,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Source",
                            value: /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex flex-wrap items-center justify-end gap-1.5",
                                children: [
                                    /*#__PURE__*/ _jsx(DutySourceChips, {
                                        programId: programId,
                                        origin: d.origin,
                                        pwsRef: d.pwsRef,
                                        letter: d.letter,
                                        dutyKey: d.dutyKey,
                                        sourceDocumentId: d.sourceDocumentId,
                                        clins: clins,
                                        clinDisplay: dutyClinDisplay(d.dutyKey, clins),
                                        testIds: {
                                            pws: Testids.cmDuty.headerPws,
                                            clin: Testids.cmDuty.headerClin,
                                            sourceDoc: Testids.cmDuty.headerSourceDoc
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(CmSourceButton, {
                                        locator: d.sourceLocator
                                    })
                                ]
                            })
                        }),
                        d.sourceLocator ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Where",
                            value: d.sourceLocator
                        }) : null,
                        promotedFrom ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            "data-testid": Testids.cmWorkProducts.history,
                            label: "Promoted from",
                            value: /*#__PURE__*/ _jsxs(SpecraLink, {
                                href: `/delivery/programs/${programId}/workstreams/${promotedFrom.workstreamId}?tab=work`,
                                renderLink: renderRouterLink,
                                children: [
                                    promotedFrom.title,
                                    " · produced ",
                                    cmDate(promotedFrom.producedOn)
                                ]
                            })
                        }) : null,
                        task !== null ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            "data-testid": Testids.cmDuty.appliesTo,
                            label: "Applies to",
                            value: `Optional Task ${task}: each exercise, from its own PoP; no dates until it is exercised`
                        }) : null,
                        d.eventKind ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Milestone",
                            value: `${eventLabel(d.eventKind, undefined, `${d.title} ${eventTexts(d.eventKind, lk.rules)}`, casing)}: held, never filed or reviewed. Mark it held when it happens; it never counts as late.`
                        }) : /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Final review",
                            value: tierLabel(d.finalReviewTier)
                        }),
                        d.endedOn ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            "data-testid": Testids.cmDuty.ended,
                            label: "Ended",
                            value: `${cmDate(d.endedOn)}; no dates after it`
                        }) : null,
                        d.acceptanceCriteria ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Acceptance",
                            value: d.acceptanceCriteria
                        }) : null
                    ]
                }) : undefined,
                children: [
                    /*#__PURE__*/ _jsx(AwaitingApprovalBanner, {
                        programId: programId,
                        duty: dutyId
                    }),
                    receipt ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "success",
                        variant: "framed",
                        "data-testid": Testids.cmDuty.ruleReceipt,
                        onDismiss: ()=>setReceipt(null),
                        children: [
                            "Rule saved: ",
                            receipt,
                            "."
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: "Rule",
                                action: access.canWrite && rules.length > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: ()=>setEditRule('new'),
                                    "data-testid": Testids.cmDuty.addRule,
                                    children: "Add rule"
                                }) : undefined
                            }),
                            rules.length === 0 && !duty.isLoading ? /*#__PURE__*/ _jsx("div", {
                                "data-testid": Testids.cmDuty.noRules,
                                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    framed: true,
                                    title: "Add the rule the award states",
                                    description: "Dates come from a rule, e.g. “Due seven days after Optional Task Exercise and monthly thereafter.” Without one, nothing is scheduled.",
                                    action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>setEditRule('new'),
                                        "data-testid": Testids.cmDuty.addRule,
                                        children: "Add rule"
                                    }) : undefined
                                })
                            }) : ruleGroups.map((g)=>{
                                const r = g.rules[0];
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1 border-l-2 border-border pl-3 text-[length:var(--fs-ui-body)]",
                                    "data-testid": Testids.cmDuty.ruleBlock,
                                    "data-row-id": r.id,
                                    children: [
                                        r.ruleText ? /*#__PURE__*/ _jsxs("blockquote", {
                                            className: "italic",
                                            "data-testid": Testids.cmDuty.ruleText,
                                            children: [
                                                "“",
                                                /*#__PURE__*/ _jsx(GlossaryText, {
                                                    text: r.ruleText
                                                }),
                                                "”"
                                            ]
                                        }) : null,
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-wrap items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    "data-testid": Testids.cmDuty.ruleReading,
                                                    children: [
                                                        "→ ",
                                                        /*#__PURE__*/ _jsx(GlossaryText, {
                                                            text: describeRule(r, (id)=>lk.dutyById.get(id)?.title)
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(CmSourceButton, {
                                                    locator: r.sourceLocator,
                                                    quote: r.ruleText
                                                }),
                                                access.canWrite && g.rules.length === 1 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "ghost",
                                                    onClick: ()=>setEditRule(r),
                                                    "data-testid": Testids.cmDuty.ruleEdit,
                                                    children: "Edit rule"
                                                }) : null
                                            ]
                                        }),
                                        ruleGroups.length > 1 || g.rules.length > 1 || r.gate !== 'base' ? /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-wrap items-center gap-x-2 text-muted-foreground",
                                            "data-testid": Testids.cmDuty.rulePlaces,
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        "Applies in ",
                                                        g.label
                                                    ]
                                                }),
                                                access.canWrite && g.rules.length > 1 ? /*#__PURE__*/ _jsxs("span", {
                                                    className: "inline-flex flex-wrap items-center gap-x-1",
                                                    children: [
                                                        "· Edit for",
                                                        g.rules.map((x, i)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                                                size: "inline",
                                                                variant: "link",
                                                                onClick: ()=>setEditRule(x),
                                                                "data-testid": Testids.cmDuty.ruleEdit,
                                                                children: g.places[i].label
                                                            }, x.id))
                                                    ]
                                                }) : null
                                            ]
                                        }) : null
                                    ]
                                }, r.id);
                            })
                        ]
                    }),
                    conditionHeld ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "warning",
                        variant: "framed",
                        "data-testid": Testids.cmDuty.conditionHeld,
                        action: /*#__PURE__*/ _jsx(SpecraLink, {
                            href: conditionHeldStatus(holdOf(d?.pwsRef ?? '')).href ?? `/delivery/programs/${programId}/docs?tab=modifications`,
                            renderLink: renderRouterLink,
                            children: "Answer the question"
                        }),
                        children: [
                            conditionHeldStatus(holdOf(d?.pwsRef ?? '')).label,
                            ". The award states this deliverable under one of several conditions; it owes no date until the condition that holds is chosen."
                        ]
                    }) : null,
                    needsStart.map((c)=>/*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "warning",
                            variant: "framed",
                            "data-testid": Testids.cmDuty.needsStart,
                            action: !access.canWrite ? undefined : c.fixVerb === 'LogContractEvent' ? /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: ()=>openLog(kindOfRule(c.ruleId), c.workstreamId),
                                "data-testid": Testids.cmDuty.logEvent,
                                children: logLabel(kindOfRule(c.ruleId))
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: ()=>fix(c),
                                "data-testid": Testids.cmDuty.checkFix,
                                children: "Set the first date"
                            }),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1.5",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            "No dates yet: this rule has no first date",
                                            c.message ? ` (${c.message})` : '',
                                            "."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(AroSuggestion, {
                                        programId: programId,
                                        check: c,
                                        canWrite: access.canWrite
                                    })
                                ]
                            })
                        }, `${c.ruleId}-${c.periodOfPerformanceId}-${c.exerciseId}`)),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: "Who owes it, by exercise"
                            }),
                            occs.length === 0 && assignments.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: "No one owes it yet: dates appear once the rule has a start and its period or exercise is on the contract."
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "space-y-3",
                                children: windowGroups(occs, lk).map((g)=>/*#__PURE__*/ _jsxs("div", {
                                        "data-testid": Testids.cmDuty.windowGroup,
                                        className: "text-[length:var(--fs-ui-body)]",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "font-medium",
                                                children: g.label
                                            }),
                                            /*#__PURE__*/ _jsx("ul", {
                                                className: "mt-1 space-y-0.5",
                                                children: g.lines.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "flex gap-3",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                className: "min-w-0 flex-1",
                                                                children: [
                                                                    l.team,
                                                                    l.exercise ? /*#__PURE__*/ _jsxs("span", {
                                                                        className: "text-muted-foreground",
                                                                        children: [
                                                                            " · ",
                                                                            l.exercise
                                                                        ]
                                                                    }) : null
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                className: "flex items-center gap-1",
                                                                children: [
                                                                    l.late > 0 ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                                        variant: "danger",
                                                                        "data-testid": Testids.cmDuty.windowLate,
                                                                        children: [
                                                                            l.late,
                                                                            " late"
                                                                        ]
                                                                    }) : null,
                                                                    l.upcoming > 0 ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                                        variant: "neutral",
                                                                        "data-testid": Testids.cmDuty.windowUpcoming,
                                                                        children: [
                                                                            l.upcoming,
                                                                            " upcoming"
                                                                        ]
                                                                    }) : null,
                                                                    l.approved > 0 ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                                        variant: "success",
                                                                        "data-testid": Testids.cmDuty.windowApproved,
                                                                        children: [
                                                                            l.approved,
                                                                            " approved"
                                                                        ]
                                                                    }) : null
                                                                ]
                                                            })
                                                        ]
                                                    }, l.key))
                                            })
                                        ]
                                    }, g.key))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-2)] border-b border-border pb-[var(--space-2)]",
                                children: [
                                    /*#__PURE__*/ _jsxs("h3", {
                                        className: "shrink-0 font-sans text-[length:var(--fs-ui-eyebrow)] font-medium uppercase leading-[var(--lh-tight)] tracking-[var(--ls-ui-group)] text-muted-foreground",
                                        children: [
                                            "Dates · ",
                                            shown.length
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "w-64",
                                        children: /*#__PURE__*/ _jsx(SpecraSearchField, {
                                            className: "w-full",
                                            value: dateQuery,
                                            onChange: (e)=>setDateQuery(e.target.value),
                                            onClear: ()=>setDateQuery(''),
                                            placeholder: "Search dates…",
                                            "data-testid": Testids.cmDuty.datesSearch
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "w-44 [&_select]:!w-full",
                                        children: /*#__PURE__*/ _jsx(DeliverableFilters, {
                                            values: {
                                                period: periodFilter
                                            },
                                            onChange: (_, value)=>setPeriodFilter(value),
                                            options: {
                                                periods: periodOptions
                                            },
                                            show: {
                                                clin: false,
                                                pws: false,
                                                team: false,
                                                status: false,
                                                sourceDoc: false,
                                                period: true
                                            },
                                            testIds: {
                                                period: Testids.cmDuty.periodFilter
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "ml-auto flex shrink-0 items-center gap-[var(--space-2)]",
                                        children: access.canWrite ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                picked.length > 0 ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                                    size: "sm",
                                                    onClick: ()=>setWaiving(true),
                                                    "data-testid": Testids.cmDuty.waiveSelected,
                                                    children: [
                                                        "Propose waiver for ",
                                                        picked.length,
                                                        " ",
                                                        picked.length === 1 ? 'date' : 'dates'
                                                    ]
                                                }) : null,
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "secondary",
                                                    onClick: ()=>setAddDate(true),
                                                    "data-testid": Testids.cmDuty.addOneOff,
                                                    children: "+ Add a date"
                                                })
                                            ]
                                        }) : null
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: "Click a date to submit it, propose a waiver, or move its due date."
                            }),
                            /*#__PURE__*/ _jsx(CmTable, {
                                caption: "Dates",
                                rows: shown,
                                columns: columns,
                                rowKey: (o)=>o.id,
                                testId: Testids.cmDuty.datesTable,
                                rowTestId: Testids.cmDuty.dateRow,
                                isLoading: dates.isLoading,
                                error: dates.error,
                                onRetry: ()=>void dates.refetch(),
                                onRowClick: (o)=>o.event?.projected ? // filing it stores it.
                                    access.canWrite ? params.open('submit', o.id, {
                                        forDuty: o.dutyId
                                    }) : undefined : params.open('occ', o.id),
                                getRowLabel: (o)=>`${d?.title ?? 'Deliverable'} due ${cmDate(o.dueOn)}`,
                                empty: occs.length > 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    size: "compact",
                                    title: "No dates match.",
                                    description: "The search and filters hide every date of this deliverable.",
                                    action: dateQuery || periodFilter || dateView.filtered ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: ()=>{
                                            setDateQuery('');
                                            setPeriodFilter('');
                                            dateView.clearAll();
                                        },
                                        "data-testid": Testids.cmColumnMenu.clearAll,
                                        children: "Clear search and filters"
                                    }) : undefined
                                }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    size: "compact",
                                    title: "No dates yet.",
                                    description: "Dates appear once the rule has a start and its period or exercise is on the contract.",
                                    action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "inline",
                                        variant: "link",
                                        onClick: ()=>setAddDate(true),
                                        children: "Add a date by hand"
                                    }) : undefined
                                })
                            })
                        ]
                    }),
                    checkGroups.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        "data-testid": Testids.cmDuty.checks,
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                className: `-mx-2 box-content flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent/50 ${specraFocusRing}`,
                                "aria-expanded": checksOpen,
                                "aria-controls": checksId,
                                onClick: ()=>setChecksOpen((o)=>!o),
                                "data-testid": Testids.cmDuty.checksToggle,
                                children: [
                                    checksOpen ? /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: "size-4 shrink-0",
                                        "aria-hidden": "true"
                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "size-4 shrink-0",
                                        "aria-hidden": "true"
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "min-w-0 flex-1",
                                        children: [
                                            /*#__PURE__*/ _jsxs(SpecraEyebrow, {
                                                children: [
                                                    "Checks (",
                                                    checkGroups.length,
                                                    ")"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                className: "block truncate",
                                                size: "caption",
                                                tone: "muted",
                                                children: "Problems with this deliverable's rule or setup, and how to fix them."
                                            })
                                        ]
                                    })
                                ]
                            }),
                            checksOpen ? /*#__PURE__*/ _jsx("div", {
                                id: checksId,
                                className: "divide-y divide-border",
                                children: checkGroups.map((group)=>{
                                    const c = group.checks[0];
                                    const count = group.checks.length;
                                    const link = fixLink(c);
                                    const canFix = !link && access.canWrite && !!FIX_LABEL[c.fixVerb];
                                    const fixLabel = link ? FIX_LABEL[c.fixVerb] ?? 'Fix' : FIX_LABEL[c.fixVerb];
                                    const expanded = expandedChecks.has(group.key);
                                    const toggle = ()=>setExpandedChecks((prev)=>{
                                            const next = new Set(prev);
                                            if (!next.delete(group.key)) next.add(group.key);
                                            return next;
                                        });
                                    // A repeated check carries its own controls, so the row itself
                                    // is not a button: the expander and the fix sit side by side.
                                    const item = count === 1 ? {
                                        id: checkRowId(c),
                                        icon: /*#__PURE__*/ _jsx(AlertTriangle, {
                                            "aria-hidden": true
                                        }),
                                        tone: 'warning',
                                        label: group.message,
                                        href: link ?? undefined,
                                        onClick: canFix ? ()=>fix(c) : undefined,
                                        meta: link || canFix ? /*#__PURE__*/ _jsx("span", {
                                            "data-testid": Testids.cmDuty.checkFix,
                                            children: fixLabel
                                        }) : undefined
                                    } : {
                                        id: group.key,
                                        icon: /*#__PURE__*/ _jsx(AlertTriangle, {
                                            "aria-hidden": true
                                        }),
                                        tone: 'warning',
                                        label: group.message,
                                        meta: `${count}×`,
                                        actions: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "inline",
                                                    variant: "link",
                                                    "aria-expanded": expanded,
                                                    onClick: toggle,
                                                    "data-testid": Testids.cmDuty.checkExpand,
                                                    children: expanded ? 'Hide' : `Show all ${count}`
                                                }),
                                                link ? /*#__PURE__*/ _jsx(SpecraLink, {
                                                    renderLink: renderRouterLink,
                                                    href: link,
                                                    "data-testid": Testids.cmDuty.checkFix,
                                                    children: fixLabel
                                                }) : canFix ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "inline",
                                                    variant: "link",
                                                    onClick: ()=>fix(c),
                                                    "data-testid": Testids.cmDuty.checkFix,
                                                    children: fixLabel
                                                }) : null
                                            ]
                                        })
                                    };
                                    return /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraActionList, {
                                                renderLink: renderRouterLink,
                                                getItemTestId: ()=>Testids.cmDuty.checkRow,
                                                items: [
                                                    item
                                                ]
                                            }),
                                            count > 1 && expanded ? /*#__PURE__*/ _jsx("ul", {
                                                className: "space-y-1 pb-3 pl-12",
                                                "data-testid": Testids.cmDuty.checkAffected,
                                                children: group.checks.map((x)=>/*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(SpecraText, {
                                                            size: "caption",
                                                            tone: "muted",
                                                            children: checkAffects(x, lk)
                                                        })
                                                    }, checkRowId(x)))
                                            }) : null
                                        ]
                                    }, group.key);
                                })
                            }) : null
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(CommentThread, {
                        programId: programId,
                        target: {
                            type: 'duty',
                            id: dutyId
                        }
                    })
                ]
            }),
            editRule ? /*#__PURE__*/ _jsx(RuleEditorDrawer, {
                programId: programId,
                dutyId: dutyId,
                rule: editRule === 'new' ? undefined : editRule,
                onFile: rules,
                onClose: ()=>setEditRule(null),
                onSaved: (r)=>{
                    setEditRule(null);
                    setReceipt(r);
                }
            }) : null,
            editDuty && d ? /*#__PURE__*/ _jsx(DutyFormDrawer, {
                programId: programId,
                duty: d,
                onClose: ()=>setEditDuty(false),
                onSaved: ()=>setEditDuty(false)
            }) : null,
            addDate ? /*#__PURE__*/ _jsx(AddDateDrawer, {
                programId: programId,
                dutyId: dutyId,
                onClose: ()=>setAddDate(false)
            }) : null,
            waiving ? /*#__PURE__*/ _jsx(WaiveDatesDrawer, {
                programId: programId,
                title: d?.title ?? 'This deliverable',
                waivable: waivable,
                selectedIds: picked,
                groups: waiveGroups(waivable, lk),
                lk: lk,
                onClose: ()=>setWaiving(false),
                onDone: ()=>{
                    setWaiving(false);
                    setSelected(new Set());
                }
            }) : null,
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: confirmDelete,
                onOpenChange: setConfirmDelete,
                title: `Delete "${d?.title ?? 'this deliverable'}"?`,
                description: "Its dates are removed with it. A deliverable with a submitted date, or one the COR waived, can't be deleted: mark those dates not owed instead.",
                isPending: deleteDuty.isPending,
                onConfirm: ()=>deleteDuty.mutate({
                        dutyId
                    }, {
                        onSuccess: ()=>navigate(`/delivery/programs/${programId}/deliverables`),
                        onSettled: ()=>setConfirmDelete(false)
                    })
            }),
            logKind !== null ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: logKind,
                milestone: isMilestone(logKind),
                workstreamId: logTeam,
                onClose: ()=>setLogKind(null)
            }) : null,
            requesting ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: "on_request",
                dutyId: dutyId,
                workstreamId: requestTeam,
                onClose: ()=>setRequesting(false)
            }) : null
        ]
    });
}
export function AddDateDrawer({ programId, dutyId, onClose }) {
    const lk = useCmLookups(programId);
    const add = useAddOccurrence(programId, {
        errors: 'inline',
        success: (r)=>`Date added: ${cmDate(r.occurrence?.dueOn)}. ${mintReceipt(r.result?.mint)}`
    });
    const [dueOn, setDueOn] = useState('');
    const [label, setLabel] = useState('');
    const [team, setTeam] = useState('');
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Add a date",
        description: "A one-off date for this deliverable. No rule schedules it, and no schedule change removes it.",
        onClose: onClose,
        busy: add.isPending,
        testId: Testids.cmDuty.addOneOffDrawer,
        footerProps: {
            confirmLabel: 'Add date',
            onConfirm: ()=>add.mutate({
                    dutyId,
                    dueOn,
                    label: label.trim(),
                    workstreamId: team
                }, {
                    onSuccess: onClose
                }),
            confirmDisabled: add.isPending,
            loading: add.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuty.addOneOffSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Label",
                    hint: "Optional, e.g. “Kickoff briefing”",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: label,
                        onChange: (e)=>setLabel(e.target.value),
                        "data-testid": Testids.cmDuty.addOneOffLabel
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Due on",
                    hint: "Leave blank if the date is not stated yet.",
                    value: dueOn,
                    onValueChange: setDueOn,
                    "data-testid": Testids.cmDuty.addOneOffDate
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Team",
                    hint: "Who owes it. Blank = contract-wide.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: team,
                        onChange: (e)=>setTeam(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Contract-wide"
                            }),
                            lk.workstreams.map((w)=>/*#__PURE__*/ _jsx("option", {
                                    value: w.id,
                                    children: w.name
                                }, w.id))
                        ]
                    })
                }),
                add.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(add.error, 'Could not save')
                }) : null
            ]
        })
    });
}
